import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../config/firebase';
import {
  doc, onSnapshot, setDoc, collection, query, orderBy, getDocs, serverTimestamp
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { computeStandings, nearestBracketSize, recommendedSwissRounds } from '../../utils/swissPairing';
import {
  generateNextSwissRound, startKnockout, generateNextKnockoutRound,
  replayDrawnPairing, isRoundComplete
} from '../../utils/tournamentEngine';
import { getTier, DEFAULT_RATING } from '../../utils/chessRating';
import Icon from '../Shared/Icons';
import { C, FONT } from '../../utils/chessTheme';

const DEFAULT_RULES = `1. Kuwa mkweli — cheza mwenyewe, bila msaada wa mtu au kifaa.
2. Ingia mechi yako kwa wakati; kuchelewa sana kunaweza kukugharimu mechi.
3. Muda wa saa ni sheria — muda ukiisha, mechi imekwisha.
4. Heshima kwa mpinzani kabla, wakati na baada ya mechi.
5. Mfumo: raundi za Swiss kwanza, kisha Knockout hadi bingwa mmoja.
6. Sare kwenye Knockout inachezwa upya kwa maelekezo ya organizer.
7. Maamuzi ya organizer wa Iyanna Foundation ni ya mwisho.
8. Coins na zawadi hugawiwa mwishoni mwa mashindano.`;

const PLAYER_KEY = 'iyanna_chess_player';
function loadPlayer() {
  try { const raw = localStorage.getItem(PLAYER_KEY); return raw ? JSON.parse(raw) : null; }
  catch { return null; }
}

export default function TournamentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const isOrganizer = userRole === 'admin' || userRole === 'staff';
  const player = loadPlayer();

  const [tournament, setTournament] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [currentRound, setCurrentRound] = useState(null);
  const [pairings, setPairings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [roundComplete, setRoundComplete] = useState(false);
  const [bracketSize, setBracketSize] = useState(8);
  const [showRules, setShowRules] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'tournaments', id), snap => {
      if (snap.exists()) setTournament({ id: snap.id, ...snap.data() });
    });
    return unsub;
  }, [id]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'tournaments', id, 'participants'), snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setParticipants(list);
      setBracketSize(nearestBracketSize(list.length));
    });
    return unsub;
  }, [id]);

  useEffect(() => {
    (async () => {
      const q = query(collection(db, 'tournaments', id, 'rounds'), orderBy('roundNumber', 'asc'));
      const snap = await getDocs(q);
      const roundList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (roundList.length > 0) setCurrentRound(roundList[roundList.length - 1]);
    })();
  }, [id, tournament?.currentRound, tournament?.currentPhase]);

  useEffect(() => {
    if (!currentRound) { setPairings([]); return; }
    const unsub = onSnapshot(collection(db, 'tournaments', id, 'rounds', currentRound.id, 'pairings'), snap => {
      setPairings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [id, currentRound]);

  useEffect(() => {
    if (!currentRound) { setRoundComplete(false); return; }
    (async () => {
      const complete = await isRoundComplete(id, currentRound.id);
      setRoundComplete(complete);
    })();
  }, [id, currentRound, pairings]);

  if (!tournament) {
    return <div style={styles.page}><div style={styles.centerMsg}>Inapakia…</div></div>;
  }

  const standings = computeStandings(participants);
  const isRegistered = player && participants.some(p => p.id === player.id);
  const isFull = participants.length >= (tournament.maxParticipants || 500);
  const recRounds = recommendedSwissRounds(participants.length);

  // Round progress
  const totalGames = pairings.length;
  const doneGames = pairings.filter(p => p.isBye || (p.result && p.result !== 'draw_pending_replay')).length;

  async function handleRegister() {
    if (!player) { navigate('/chess'); return; }
    setBusy(true);
    try {
      await setDoc(doc(db, 'tournaments', id, 'participants', player.id), {
        id: player.id, name: player.name, rating: player.rating || DEFAULT_RATING,
        score: 0, opponents: [], colorHistory: { w: 0, b: 0 }, byes: 0,
        registeredAt: serverTimestamp(),
      });
    } catch (e) { console.error(e); }
    setBusy(false);
  }
  async function handleStartSwiss() {
    setBusy(true);
    try { await generateNextSwissRound(id, tournament.clockSeconds || 900); }
    catch (e) { console.error(e); }
    setBusy(false);
  }
  async function handleStartKnockout() {
    setBusy(true);
    try { await startKnockout(id, bracketSize, tournament.clockSeconds || 900); }
    catch (e) { console.error(e); }
    setBusy(false);
  }
  async function handleNextKnockoutRound() {
    setBusy(true);
    try { await generateNextKnockoutRound(id, currentRound.id, currentRound.roundNumber); }
    catch (e) { console.error(e); }
    setBusy(false);
  }
  async function handleReplay(pairingId) {
    setBusy(true);
    try { await replayDrawnPairing(id, currentRound.id, pairingId, tournament.clockSeconds || 900); }
    catch (e) { console.error(e); }
    setBusy(false);
  }

  const myPairing = player ? pairings.find(p => p.player1Id === player.id || p.player2Id === player.id) : null;

  // Status banner
  const status = tournament.status;
  const statusInfo =
    status === 'registration' ? { label: 'Usajili Uko Wazi', sub: 'Jiunge kabla mashindano hayajaanza', color: '#5FD08A' } :
    status === 'swiss' ? { label: `Swiss — Raundi ${tournament.currentRound}`, sub: `Michezo ${doneGames}/${totalGames} imekamilika`, color: '#34D399' } :
    status === 'knockout' ? { label: `Knockout — Raundi ${tournament.currentRound}`, sub: `Michezo ${doneGames}/${totalGames} imekamilika`, color: '#FBBF24' } :
    { label: 'Mashindano Yamekamilika', sub: tournament.winnerName ? `Bingwa: ${tournament.winnerName}` : '', color: C.textDim };

  function resultBadge(p) {
    if (p.result === 'player1') return { text: '1–0', color: '#5FD08A' };
    if (p.result === 'player2') return { text: '0–1', color: '#5FD08A' };
    if (p.result === 'draw') return { text: '½–½', color: C.textDim };
    if (p.result === 'draw_pending_replay') return { text: 'Sare — inachezwa upya', color: '#FBBF24' };
    if (p.isBye) return { text: 'BYE', color: C.textFaint };
    if (p.gameId) return { text: 'Inaendelea', color: '#FBBF24' };
    return { text: '', color: C.textFaint };
  }

  return (
    <div style={styles.page}>
      <div style={styles.bar}>
        <button style={styles.backIconBtn} onClick={() => navigate('/chess/tournaments')}>
          <Icon name="door" size={19} color={C.text} strokeWidth={2.4} />
        </button>
        <div style={styles.barTitle}>Mashindano</div>
        <button style={styles.backIconBtn} onClick={() => setShowRules(true)} title="Rules">
          <Icon name="info" size={18} color={C.text} strokeWidth={2.2} />
        </button>
      </div>

      {/* TITLE */}
      <div style={styles.titleBlock}>
        <div style={styles.title}>{tournament.name}</div>
        {tournament.description && <div style={styles.desc}>{tournament.description}</div>}
        <div style={styles.chipRow}>
          <span style={styles.chip}><Icon name="user" size={12} color={C.textDim} /> {participants.length}/{tournament.maxParticipants}</span>
          <span style={styles.chip}><Icon name="clock" size={12} color={C.textDim} /> {Math.round((tournament.clockSeconds || 900) / 60)} min</span>
          {tournament.prizes && <span style={{ ...styles.chip, color: C.accent, borderColor: 'rgba(52,211,153,0.4)' }}><Icon name="gift" size={12} color={C.accent} /> {tournament.prizes}</span>}
        </div>
      </div>

      {/* STATUS BANNER */}
      <div style={{ ...styles.statusBanner, borderColor: statusInfo.color + '55' }}>
        <span style={{ ...styles.statusDot, background: statusInfo.color }} />
        <div style={{ flex: 1 }}>
          <div style={styles.statusLabel}>{statusInfo.label}</div>
          {statusInfo.sub && <div style={styles.statusSub}>{statusInfo.sub}</div>}
        </div>
        {status === 'finished' && <Icon name="crown" size={20} color={C.accent} strokeWidth={2} />}
      </div>

      {/* MY NEXT MATCH — prominent */}
      {myPairing && myPairing.gameId && !myPairing.result && (
        <button style={styles.myMatch} onClick={() => navigate(`/chess/room/${myPairing.gameId}`)}>
          <div style={styles.myMatchLeft}>
            <div style={styles.myMatchLabel}>Mechi Yako Sasa</div>
            <div style={styles.myMatchNames}>{myPairing.player1Name} vs {myPairing.player2Name}</div>
          </div>
          <span style={styles.myMatchGo}>Cheza →</span>
        </button>
      )}
      {myPairing && myPairing.isBye && (
        <div style={styles.byeCard}>Raundi hii una BYE — unapata pointi moja bila kucheza.</div>
      )}

      {/* REGISTRATION */}
      {status === 'registration' && (
        <div style={styles.card}>
          {!player ? (
            <div style={styles.hint}>Weka jina lako kwenye Chess Lobby kwanza ili ujiunge.</div>
          ) : isRegistered ? (
            <div style={styles.success}><Icon name="check" size={15} color="#5FD08A" strokeWidth={2.5} /> Umejiunga — subiri mashindano yaanze.</div>
          ) : isFull ? (
            <div style={styles.hint}>Nafasi zimejaa ({tournament.maxParticipants}).</div>
          ) : (
            <>
              <button style={styles.primaryBtn} onClick={handleRegister} disabled={busy}>
                {busy ? 'Inajiunga…' : 'Jiunge na Mashindano'}
              </button>
              <div style={styles.regHint}>
                Kwa kujiunga unakubali <button style={styles.linkBtn} onClick={() => setShowRules(true)}>rules</button>.
              </div>
            </>
          )}
        </div>
      )}

      {/* ORGANIZER */}
      {isOrganizer && status !== 'finished' && (
        <div style={styles.orgPanel}>
          <div style={styles.orgLabel}><Icon name="wrench" size={12} color={C.accent} /> Organizer</div>

          {status === 'registration' && (
            <>
              <button style={styles.orgPrimary} onClick={handleStartSwiss} disabled={busy || participants.length < 2}>
                Anzisha Swiss — Raundi 1
              </button>
              <div style={styles.orgHint}>Washiriki {participants.length} · inashauriwa raundi {recRounds} za Swiss.</div>
            </>
          )}

          {status === 'swiss' && (
            <>
              <button style={styles.orgPrimary} onClick={handleStartSwiss} disabled={busy || !roundComplete}>
                Raundi Ifuatayo ya Swiss
              </button>
              <div style={styles.orgDivider}>au maliza Swiss, anza Knockout</div>
              <div style={styles.orgInline}>
                <span style={styles.orgHint}>Ukubwa wa bracket:</span>
                <select style={styles.select} value={bracketSize} onChange={e => setBracketSize(Number(e.target.value))}>
                  {[2, 4, 8, 16, 32, 64].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <button style={styles.orgAlt} onClick={handleStartKnockout} disabled={busy || !roundComplete}>
                  Anza Knockout
                </button>
              </div>
              {!roundComplete && <div style={styles.orgWarn}>Bado kuna michezo ya raundi hii haijaisha ({doneGames}/{totalGames}).</div>}
            </>
          )}

          {status === 'knockout' && (
            <>
              <button style={styles.orgPrimary} onClick={handleNextKnockoutRound} disabled={busy || !roundComplete}>
                Raundi Ifuatayo ya Knockout
              </button>
              {!roundComplete && <div style={styles.orgWarn}>Bado kuna michezo haijaisha ({doneGames}/{totalGames}).</div>}
            </>
          )}
        </div>
      )}

      {/* PRIZES & COINS */}
      <div style={styles.prizeCard}>
        <div style={styles.prizeTitle}>Zawadi & Coins</div>
        <div style={styles.prizeText}>
          Kila mshiriki anapata coins = pointi × 10. Bingwa anaongezwa coins 100 na anaingia Hall of Fame milele.
          Mechi za tournament zina pointi kubwa zaidi za rating.
        </div>
      </div>

      {/* CURRENT ROUND */}
      {currentRound && (
        <>
          <div style={styles.sectionLabel}>
            {currentRound.phase === 'knockout' ? 'Knockout' : 'Swiss'} · Raundi {currentRound.roundNumber}
          </div>
          <div style={styles.list}>
            {pairings.map(p => {
              const badge = resultBadge(p);
              const mine = player && (p.player1Id === player.id || p.player2Id === player.id);
              return (
                <div
                  key={p.id}
                  style={{ ...styles.pairingRow, ...(mine ? styles.pairingMine : {}) }}
                  onClick={() => p.gameId && navigate(`/chess/room/${p.gameId}`)}
                >
                  <div style={styles.pairingNames}>
                    {p.isBye ? p.player1Name : <>{p.player1Name} <span style={styles.vs}>vs</span> {p.player2Name}</>}
                  </div>
                  <span style={{ ...styles.badge, color: badge.color, borderColor: badge.color + '44' }}>{badge.text}</span>
                  {isOrganizer && p.result === 'draw_pending_replay' && (
                    <button style={styles.replayBtn} onClick={(e) => { e.stopPropagation(); handleReplay(p.id); }} disabled={busy}>
                      Cheza upya
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* STANDINGS */}
      <div style={styles.sectionLabel}>Msimamo</div>
      <div style={styles.list}>
        {standings.length === 0 ? (
          <div style={styles.hint}>Hakuna washiriki bado.</div>
        ) : standings.map((p, i) => {
          const tier = getTier(p.rating || DEFAULT_RATING);
          const mine = player && p.id === player.id;
          const top = i === 0 && status !== 'registration';
          return (
            <div key={p.id} style={{ ...styles.standRow, ...(mine ? styles.standMine : {}) }}>
              <span style={{ ...styles.standRank, ...(top ? styles.standRankTop : {}) }}>
                {top ? <Icon name="crown" size={13} color="#06281A" strokeWidth={2.4} /> : i + 1}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.standName}>{p.name}{mine && <span style={styles.meTag}> · wewe</span>}</div>
                <div style={{ ...styles.standTier, color: tier.color }}>{tier.swahili}</div>
              </div>
              <span style={styles.standScore}>{p.score}<span style={styles.standPts}> pts</span></span>
            </div>
          );
        })}
      </div>

      {/* RULES MODAL */}
      {showRules && (
        <div style={styles.rulesOverlay} onClick={() => setShowRules(false)}>
          <div style={styles.rulesCard} onClick={e => e.stopPropagation()}>
            <div style={styles.rulesTitle}>Rules za Mashindano</div>
            <div style={styles.rulesBody}>
              {(tournament.rules || DEFAULT_RULES).split('\n').map((line, i) => (
                <div key={i} style={styles.ruleLine}>{line}</div>
              ))}
            </div>
            <button style={styles.rulesClose} onClick={() => setShowRules(false)}>Nimeelewa</button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: C.bgGradient, padding: '20px 16px 130px', maxWidth: '640px', margin: '0 auto' },
  bar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' },
  backIconBtn: { width: '38px', height: '38px', borderRadius: '11px', background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.line}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  barTitle: { color: C.text, fontFamily: FONT.display, fontSize: '19px', fontWeight: '800' },
  centerMsg: { color: C.text, textAlign: 'center', padding: '80px 20px', fontSize: '14px' },

  titleBlock: { marginBottom: '16px' },
  title: { color: C.text, fontFamily: FONT.display, fontSize: '25px', fontWeight: '800', letterSpacing: '-0.3px' },
  desc: { color: C.textDim, fontSize: '13px', marginTop: '5px', lineHeight: 1.5 },
  chipRow: { display: 'flex', gap: '7px', flexWrap: 'wrap', marginTop: '12px' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.line}`, color: C.textDim, fontSize: '11px', fontWeight: '700', padding: '6px 12px', borderRadius: '99px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  statusBanner: { display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(255,255,255,0.04)', border: '1px solid', borderRadius: '15px', padding: '14px 16px', marginBottom: '14px' },
  statusDot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
  statusLabel: { color: C.text, fontSize: '15px', fontWeight: '800' },
  statusSub: { color: C.textDim, fontSize: '12px', marginTop: '2px' },

  myMatch: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', background: 'linear-gradient(135deg, rgba(52,211,153,0.18), rgba(14,159,110,0.06))', border: '1.5px solid rgba(52,211,153,0.5)', borderRadius: '15px', padding: '15px 17px', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '14px', textAlign: 'left' },
  myMatchLeft: { flex: 1, minWidth: 0 },
  myMatchLabel: { color: C.accent, fontSize: '11px', fontWeight: '800', letterSpacing: '0.5px', textTransform: 'uppercase' },
  myMatchNames: { color: C.text, fontSize: '14.5px', fontWeight: '800', marginTop: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  myMatchGo: { color: '#06281A', background: 'linear-gradient(135deg, #6EE7A8, #34D399)', fontSize: '12.5px', fontWeight: '800', padding: '9px 15px', borderRadius: '11px', flexShrink: 0 },
  byeCard: { background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.line}`, borderRadius: '13px', padding: '13px 15px', color: C.textDim, fontSize: '12.5px', marginBottom: '14px' },

  card: { background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.line}`, borderRadius: '15px', padding: '18px', textAlign: 'center', marginBottom: '14px' },
  hint: { color: C.textDim, fontSize: '12.5px', lineHeight: 1.5 },
  success: { display: 'inline-flex', alignItems: 'center', gap: '7px', color: '#5FD08A', fontSize: '13.5px', fontWeight: '800' },
  primaryBtn: { background: 'linear-gradient(135deg, #0E9F6E, #34D399)', border: 'none', color: '#06281A', padding: '14px 28px', borderRadius: '12px', fontSize: '14.5px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
  regHint: { color: C.textFaint, fontSize: '11.5px', marginTop: '11px' },
  linkBtn: { background: 'transparent', border: 'none', color: C.accent, fontSize: '11.5px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline', padding: 0 },

  orgPanel: { background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.28)', borderRadius: '15px', padding: '16px', marginBottom: '14px' },
  orgLabel: { display: 'inline-flex', alignItems: 'center', gap: '6px', color: C.accent, fontSize: '11px', fontWeight: '800', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '12px' },
  orgPrimary: { width: '100%', background: 'linear-gradient(135deg, #0E9F6E, #34D399)', border: 'none', color: '#06281A', padding: '13px', borderRadius: '11px', fontSize: '13.5px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
  orgHint: { color: C.textDim, fontSize: '11.5px', marginTop: '9px' },
  orgDivider: { color: C.textFaint, fontSize: '11px', textAlign: 'center', margin: '12px 0 10px' },
  orgInline: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  orgAlt: { background: 'rgba(255,255,255,0.08)', border: `1px solid ${C.lineStrong}`, color: C.text, padding: '10px 16px', borderRadius: '10px', fontSize: '12.5px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
  orgWarn: { color: '#FBBF24', fontSize: '11.5px', marginTop: '10px', fontWeight: '700' },
  select: { background: 'rgba(255,255,255,0.08)', border: `1px solid ${C.lineStrong}`, color: C.text, borderRadius: '9px', padding: '9px 10px', fontSize: '12.5px', fontFamily: 'inherit' },

  prizeCard: { background: 'linear-gradient(150deg, rgba(52,211,153,0.09), rgba(52,211,153,0.02))', border: '1px solid rgba(52,211,153,0.28)', borderRadius: '15px', padding: '15px 17px', marginBottom: '6px' },
  prizeTitle: { color: C.accent, fontSize: '11px', fontWeight: '800', letterSpacing: '0.7px', textTransform: 'uppercase', marginBottom: '6px' },
  prizeText: { color: C.textDim, fontSize: '12px', lineHeight: 1.6 },

  sectionLabel: { color: C.textDim, fontSize: '11.5px', fontWeight: '800', letterSpacing: '0.7px', textTransform: 'uppercase', margin: '20px 0 10px' },
  list: { display: 'flex', flexDirection: 'column', gap: '7px' },
  pairingRow: { display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.line}`, borderRadius: '13px', padding: '12px 14px', cursor: 'pointer' },
  pairingMine: { border: '1.5px solid rgba(52,211,153,0.5)', background: 'rgba(52,211,153,0.08)' },
  pairingNames: { color: C.text, fontSize: '13px', fontWeight: '700', flex: 1, minWidth: 0 },
  vs: { color: C.textFaint, fontWeight: '600', fontSize: '11.5px' },
  badge: { fontSize: '11.5px', fontWeight: '800', padding: '4px 10px', borderRadius: '99px', border: '1px solid', flexShrink: 0 },
  replayBtn: { background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.4)', color: '#FBBF24', padding: '6px 11px', borderRadius: '9px', fontSize: '11px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 },

  standRow: { display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.line}`, borderRadius: '13px', padding: '11px 14px' },
  standMine: { border: '1.5px solid rgba(52,211,153,0.5)', background: 'rgba(52,211,153,0.08)' },
  standRank: { width: '30px', height: '30px', borderRadius: '9px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.06)', color: C.textDim, fontSize: '12.5px', fontWeight: '800' },
  standRankTop: { background: 'linear-gradient(135deg, #6EE7A8, #34D399)' },
  standName: { color: C.text, fontSize: '13.5px', fontWeight: '800', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  meTag: { color: C.accent, fontWeight: '800' },
  standTier: { fontSize: '10.5px', fontWeight: '700', marginTop: '2px' },
  standScore: { color: C.accent, fontFamily: FONT.display, fontSize: '17px', fontWeight: '800', flexShrink: 0 },
  standPts: { color: C.textFaint, fontSize: '10px', fontWeight: '700' },

  rulesOverlay: { position: 'fixed', inset: 0, zIndex: 970, background: 'rgba(10,13,12,0.82)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  rulesCard: { width: '100%', maxWidth: '400px', maxHeight: '80vh', overflowY: 'auto', background: 'linear-gradient(180deg, #2A302D, #1E2421)', border: `1px solid ${C.lineStrong}`, borderRadius: '18px', padding: '22px' },
  rulesTitle: { color: C.text, fontFamily: FONT.display, fontSize: '19px', fontWeight: '800', marginBottom: '14px', textAlign: 'center' },
  rulesBody: { display: 'flex', flexDirection: 'column', gap: '9px', marginBottom: '18px' },
  ruleLine: { color: C.textDim, fontSize: '13px', lineHeight: 1.6 },
  rulesClose: { width: '100%', background: 'linear-gradient(135deg, #0E9F6E, #34D399)', color: '#06281A', border: 'none', padding: '13px', borderRadius: '12px', fontSize: '13.5px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
};
